import { useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { POKE, useFace } from "@softmax/avatar/expressions";
import { pageQuestion } from "./chat/chat-view";
import { useWidgetActions } from "./use-widget-actions";
import { useWidgetCharacter } from "./use-widget-character";
import { WidgetCharacter } from "./widget-character";
import { WidgetChat } from "./chat/widget-chat";
import { useWidgetNavigation } from "./use-widget-navigation";
import { useWidgetNotices } from "./notices/use-widget-notices";
import { WidgetNotices } from "./notices/widget-notices";
import { useWidgetPosition } from "./placement/use-widget-position";
import type { CogentWidgetProps } from "./widget-types";
import s from "./cogent.module.css";
export function CogentWidget(props: CogentWidgetProps) {
  return <Widget key={props.cogent?.id ?? "signed-out"} {...props} />;
}
function Widget(props: CogentWidgetProps) {
  const ids = useId();
  const name = props.cogent?.name ?? "your cogent";
  const live = props.live;
  const position = useWidgetPosition({
    storageKey: props.storageKey ?? "softmax-cogent-corner",
    hasCogent: props.cogent !== null,
  });
  const character = useWidgetCharacter(props);
  const actions = useWidgetActions({
    props,
    live,
  });
  const navigation = useWidgetNavigation({
    props,
    position,
    onBubbleClose: () => actions.setReleasing(false),
  });
  const { face, play: playFace } = useFace(position.clip, true);
  const notices = useWidgetNotices({
    live: props.disabled ? null : live,
    pageContext: props.page ?? null,
    cogentId: props.cogent?.id ?? null,
    navigation,
    playFace,
  });
  const state =
    props.cogent?.state === "here" && live?.status.kind === "awake"
      ? "here"
      : "away";
  // A page question only exists while the cogent may act, and nobody could answer it unseen. Anything else waits for
  // the viewer to open the chat: opening it for them would hand the page back after they closed it.
  const askingAboutPage = live?.question?.id === pageQuestion;
  const { setOpen } = navigation;
  useEffect(() => {
    if (askingAboutPage) setOpen(true);
  }, [askingAboutPage, setOpen]);
  const toggle = () => {
    if (navigation.open) navigation.close();
    else {
      navigation.setOpen(true);
      playFace(POKE.face, POKE.ms);
    }
  };
  const { root: rootRef, host, corner, facing, clip } = position;

  if (!host) return null;

  return createPortal(
    <div
      inert={props.disabled}
      ref={rootRef}
      className={s.dock}
      style={character.style}
      data-corner={corner}
      data-open={navigation.open ? "" : undefined}
      data-placeholder={props.cogent ? undefined : ""}
      data-drawn={character.drawn}
      data-facing={facing}
      data-clip={clip ?? undefined}
      data-site-cogent
      data-corner-fit
      data-testid="cogent-panel"
    >
      <WidgetCharacter
        props={props}
        character={character}
        position={position}
        ids={ids}
        name={name}
        state={state}
        face={face}
        activity={actions.activity}
        open={navigation.open}
        toggle={toggle}
        status={navigation.open || notices.remark ? null : actions.statusLine}
      />
      <WidgetNotices name={name} navigation={navigation} notices={notices} />
      <WidgetChat
        ids={ids}
        open={navigation.open}
        name={name}
        state={state}
        props={props}
        live={live}
        actions={actions}
        panel={navigation.panel}
        log={navigation.log}
        close={navigation.close}
      />
    </div>,
    host,
  );
}
